//
// celestrak.ts — real satellite orbits
//
// Translation of `Networking/CelesTrakService.swift`.
//
// CelesTrak's GP API serves whole named groups (`GROUP=starlink`,
// `GROUP=stations`, …) as JSON with the six Keplerian elements already
// parsed out — no fixed-width TLE decoding. Those elements feed the real
// propagator in `orbital/satellite.ts`, so ground tracks are computed from
// where a satellite actually is rather than from a seeded sine wave.
//
// Free, no key, CORS-open. CelesTrak asks that clients not re-fetch a group
// more than about once every couple of hours — element sets are only issued
// a few times a day — hence the cache. Hammering it is what gets an IP
// blocked.
//

import type { ContactKey, ObjectCategory, OrbitalElements, TrackedObject } from "../types";
import { CELESTRAK_GP, fetchJSON } from "./config";
import { altitudeKm, periodMinutes, propagate, velocityKms } from "../orbital/satellite";
import { fallbackSatellites } from "./satellites-fallback";

export type Group =
  | "stations" | "starlink" | "gps-ops" | "weather"
  | "science" | "galileo" | "geo" | "oneweb"
  | "active" | "visual" | "last-30-days";

const GROUP_NAMES: Record<Group, string> = {
  stations: "Space Stations",
  starlink: "Starlink",
  "gps-ops": "GPS Operational",
  weather: "Weather Satellites",
  science: "Science Missions",
  galileo: "Galileo",
  geo: "Geostationary",
  oneweb: "OneWeb",
  active: "All Active",
  visual: "Brightest / Visual",
  "last-30-days": "Launched Last 30 Days",
};

/** CelesTrak's OMM-style JSON, SCREAMING_SNAKE on the wire. */
interface GPRecord {
  OBJECT_NAME: string;
  OBJECT_ID?: string;
  EPOCH: string;
  MEAN_MOTION: number;
  ECCENTRICITY: number;
  INCLINATION: number;
  RA_OF_ASC_NODE: number;
  ARG_OF_PERICENTER: number;
  MEAN_ANOMALY: number;
  NORAD_CAT_ID: number;
  BSTAR?: number;
}

const CACHE_TTL_MS = 2 * 3600 * 1000;
const cache = new Map<Group, { fetchedAt: number; objects: TrackedObject[] }>();

/**
 * CelesTrak epochs look like "2026-08-21T15:51:23.829408" — ISO 8601 with
 * no timezone designator (they are UTC by definition) and microsecond
 * precision. `Date.parse` would read that as *local* time, silently
 * shifting every satellite by the viewer's UTC offset, so the Z is added
 * explicitly.
 */
function parseEpochMs(raw: string): number {
  const withZone = raw.endsWith("Z") ? raw : `${raw}Z`;
  const ms = Date.parse(withZone);
  return Number.isNaN(ms) ? Date.now() : ms;
}

function relativeEpoch(epochMs: number): string {
  const hours = (Date.now() - epochMs) / 3600000;
  if (hours < 1) return "fresh";
  if (hours < 48) return `${hours.toFixed(0)}h old`;
  return `${(hours / 24).toFixed(0)}d old`;
}

function typeLabel(upperName: string, group: Group, elements: OrbitalElements): string {
  if (upperName.includes("ISS") || upperName.includes("ZARYA")) return "CREWED SPACE STATION";
  if (upperName.includes("CSS") || upperName.includes("TIANHE")) return "CREWED SPACE STATION";
  if (upperName.includes("STARLINK")) return "STARLINK BROADBAND SATELLITE";
  if (upperName.includes("R/B")) return "SPENT ROCKET BODY";
  if (upperName.includes("DEB")) return "ORBITAL DEBRIS";

  // Regime by altitude — more informative than the group name alone.
  const alt = altitudeKm(elements);
  const regime = alt > 30000 ? "GEO" : alt > 5000 ? "MEO" : "LEO";
  return `${regime} — ${GROUP_NAMES[group].toUpperCase()}`;
}

function toTrackedObject(record: GPRecord, group: Group): TrackedObject {
  const elements: OrbitalElements = {
    meanMotion: record.MEAN_MOTION,
    eccentricity: record.ECCENTRICITY,
    inclination: record.INCLINATION,
    raan: record.RA_OF_ASC_NODE,
    argOfPericenter: record.ARG_OF_PERICENTER,
    meanAnomaly: record.MEAN_ANOMALY,
    epochMs: parseEpochMs(record.EPOCH),
    noradCatId: record.NORAD_CAT_ID,
    objectId: record.OBJECT_ID,
    bstar: record.BSTAR,
  };

  const name = record.OBJECT_NAME;
  const upper = name.toUpperCase();

  // Which of SAT's four second-tier buttons owns this one. Build 1.5, item
  // 7. The group it arrived in already says — nothing has to be guessed
  // from the name.
  const layer: ContactKey =
    group === "starlink" ? "satStarlink"
    : group === "stations" ? "satStations"
    : "satNavWeather";
  const category: ObjectCategory = upper.includes("STARLINK")
    ? "starlink"
    : upper.includes("DEB")
      ? "debris"
      : upper.includes("R/B")
        ? "rocket_body"
        : "active";

  // Position is recomputed every frame; this is just the value at epoch so
  // the object is never nonsensical before the first propagation.
  const atEpoch = propagate(elements, elements.epochMs);

  return {
    id: `sat-${record.NORAD_CAT_ID}`,
    noradId: record.NORAD_CAT_ID,
    name,
    category,
    typeLabel: typeLabel(upper, group, elements),
    layer,
    latitude: atEpoch.lat,
    longitude: atEpoch.lon,
    altitudeKm: altitudeKm(elements),
    velocityKms: velocityKms(elements),
    orbitalPeriodMin: periodMinutes(elements),
    inclinationDeg: elements.inclination,
    dataSource: `CelesTrak GP (${GROUP_NAMES[group]})`,
    lastUpdated: `Elements ${relativeEpoch(elements.epochMs)}`,
    elements,
  };
}

/**
 * One group, capped at `limit`. Starlink alone is ~8,000 satellites — far
 * past what the canvas can redraw per frame — so the cap is a render
 * budget, applied after the decode.
 */
export async function fetchGroup(group: Group, limit = 60): Promise<TrackedObject[]> {
  const cached = cache.get(group);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.objects.slice(0, limit);
  }

  const url = `${CELESTRAK_GP}?GROUP=${encodeURIComponent(group)}&FORMAT=json`;
  const records = await fetchJSON<GPRecord[]>(url, 25000);
  if (!records || !Array.isArray(records)) return [];

  const objects = records.map((r) => toTrackedObject(r, group));
  cache.set(group, { fetchedAt: Date.now(), objects });
  return objects.slice(0, limit);
}

/**
 * The default satellite picture — a readable slice of each group rather
 * than everything CelesTrak has. Starlink gets the largest share because a
 * constellation only reads as a constellation in numbers.
 */
export async function fetchDefaultConstellations(): Promise<TrackedObject[]> {
  const groups: Array<[Group, number]> = [
    ["stations", 8],
    ["starlink", 90],
    ["gps-ops", 12],
    ["weather", 12],
    ["science", 10],
    ["geo", 10],
  ];

  const batches = await Promise.all(groups.map(([g, n]) => fetchGroup(g, n)));

  // A satellite can legitimately appear in two groups (the ISS is in both
  // `stations` and `visual`), so dedupe on NORAD ID.
  const seen = new Set<number>();
  const combined: TrackedObject[] = [];
  for (const batch of batches) {
    for (const obj of batch) {
      if (obj.noradId !== undefined) {
        if (seen.has(obj.noradId)) continue;
        seen.add(obj.noradId);
      }
      combined.push(obj);
    }
  }

  // CelesTrak unreachable entirely — a plausible sky beats a blank one, and
  // the fallback labels itself as approximate so it cannot be mistaken for
  // tracking.
  if (combined.length === 0) return fallbackSatellites();

  // The partial case, which is the common one: CelesTrak refuses groups
  // individually, not wholesale. Its 403 is per-group ("GP data has not
  // updated since your last successful download of GROUP=starlink"), so a
  // load can come back with every constellation except the one whose window
  // has not rolled over yet. Top up only the missing category, so a live
  // catalogue is never diluted by approximations it did not need.
  if (!combined.some((o) => o.category === "starlink")) {
    combined.push(...fallbackSatellites().filter((o) => o.category === "starlink"));
  }

  return combined;
}
